import type { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { color, diagramStyle } from './palette';

/**
 * Tenant → workspace → engagement → funds: a tenant with its tier holds a workspace, which holds entity groups (the
 * fund hierarchy); an onboarding engagement comes in from outside as scope → sources → mapping → go-live.
 */
export const TenantWorkspaceEngagement: FC = () => {
  const theme = useTheme();
  const sub = { fill: color.muted, fontSize: 13 } as const;
  const lbl = { fontSize: 15, fontWeight: 600 } as const;
  const row = { fontSize: 14, fill: color.fg } as const;
  const stages = ['scope', 'sources', 'mapping', 'go-live'];
  const node = (x: number, y: number, w: number, text: string) => (
    <g key={`${x}-${y}`}>
      <rect x={x} y={y} width={w} height="28" rx="4" fill={color.muted} opacity=".14" />
      <text x={x + w / 2} y={y + 19} textAnchor="middle" style={row}>
        {text}
      </text>
    </g>
  );

  return (
    <svg viewBox="0 0 940 360" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Tenant → workspace → engagement → funds" style={diagramStyle(theme)}>
      <defs>
        <marker id="academy-arrow-tenant" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill={color.muted} />
        </marker>
      </defs>
      {/* The engagement, coming in from outside */}
      <text x="105" y="50" textAnchor="middle" style={lbl} fill={color.fg}>
        Engagement
      </text>
      {stages.map((stage, i) => (
        <g key={stage}>
          <rect x="20" y={70 + i * 60} width="170" height="36" rx="6" fill="none" stroke={color.muted} strokeWidth="2" />
          <text x="105" y={93 + i * 60} textAnchor="middle" style={row}>
            {stage}
          </text>
          {i < stages.length - 1 && <line x1="105" y1={108 + i * 60} x2="105" y2={126 + i * 60} stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow-tenant)" />}
        </g>
      ))}
      <line x1="192" y1="268" x2="266" y2="268" stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow-tenant)" />
      {/* Tenant */}
      <rect x="270" y="30" width="650" height="300" rx="14" fill="none" stroke={color.accent} strokeWidth="2.5" />
      <text x="290" y="60" style={lbl} fill={color.accent}>
        Tenant
      </text>
      <text x="360" y="60" style={sub}>
        one firm
      </text>
      <rect x="790" y="42" width="110" height="26" rx="13" fill={color.gold} opacity=".25" />
      <text x="845" y="60" textAnchor="middle" fontSize="14" fontWeight="600" fill={color.fg}>
        tier: growth
      </text>
      {/* Workspace */}
      <rect x="300" y="85" width="590" height="220" rx="12" fill="none" stroke={color.silver} strokeWidth="2" />
      <text x="320" y="113" style={lbl} fill={color.silver}>
        Workspace
      </text>
      {/* Entity groups: the fund hierarchy */}
      <rect x="330" y="135" width="530" height="150" rx="10" fill="none" stroke={color.muted} strokeWidth="1.5" />
      <text x="350" y="160" fontSize="14" fontWeight="600" fill={color.fg}>
        Entity groups
      </text>
      <text x="460" y="160" style={sub}>
        the fund hierarchy
      </text>
      {node(380, 180, 110, 'Fund I')}
      {node(380, 240, 110, 'Fund II')}
      {node(600, 174, 130, 'Vehicle A')}
      {node(600, 206, 130, 'Vehicle B')}
      {node(600, 240, 130, 'Vehicle C')}
      <g stroke={color.muted} strokeWidth="1.5" fill="none">
        <path d="M490,194 H545 V188 H598" />
        <path d="M545,194 V220 H598" />
        <path d="M490,254 H598" />
      </g>
    </svg>
  );
};
